import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Input, StatusPill } from '@agms/ui'
import type { StatusTone } from '@agms/ui'
import api from '../lib/api'
import type { PaginatedResponse } from '../lib/api'
import type { AccountGroupRecord } from '../lib/accountGroups'
import { formatCurrencyWithCentsOrDash } from '../lib/format'
import { usePermissions } from '../hooks/useAuth'
import { apiErrorMessage } from '../hooks/useErrorHandler'
import { PageHeader, SurfaceCard } from '../components/ui'
import { useMerchants } from './merchants/useMerchantData'
import type { Merchant, MerchantFilters } from './merchants/useMerchantData'

interface ImportMerchantForm {
  fiserv_mid: string
  dba_name: string
  legal_name: string
  business_email: string
  business_phone: string
}

const emptyImportForm: ImportMerchantForm = {
  fiserv_mid: '',
  dba_name: '',
  legal_name: '',
  business_email: '',
  business_phone: '',
}

function merchantStatusTone(status: string): StatusTone {
  if (status === 'active') return 'success'
  if (status === 'suspended') return 'warning'
  if (status === 'terminated') return 'danger'
  return 'neutral'
}

export function MerchantsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const { canImportMerchants, canViewDataHealth } = usePermissions()
  const showDataHealth = canViewDataHealth()
  const showImportMerchant = canImportMerchants()

  const [filters, setFilters] = useState<MerchantFilters>({
    page: 1,
    per_page: 25,
    merchant: searchParams.get('merchant') || undefined,
    search: searchParams.get('search') || undefined,
    account_group: searchParams.get('account_group') || undefined,
    status: searchParams.get('status') || undefined,
    no_batch_7_days: searchParams.get('no_batch_7_days') === 'true' || undefined,
    has_open_chargebacks: searchParams.get('has_open_chargebacks') === 'true' || undefined,
    first_batch_this_week: searchParams.get('first_batch_this_week') === 'true' || undefined,
    sync_stale: (showDataHealth && searchParams.get('sync_stale') === 'true') || undefined,
    sync_failed: (showDataHealth && searchParams.get('sync_failed') === 'true') || undefined,
    sort_by: showDataHealth
      ? searchParams.get('sort_by') || 'created_at'
      : searchParams.get('sort_by') === 'last_transaction_sync_at'
        ? 'created_at'
        : searchParams.get('sort_by') || 'created_at',
    sort_dir: searchParams.get('sort_dir') === 'asc' ? 'asc' : 'desc',
  })
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '')
  const [showImportModal, setShowImportModal] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const [importForm, setImportForm] = useState<ImportMerchantForm>(emptyImportForm)

  const { data: merchants, isLoading, isError } = useMerchants(filters)
  const groupsQuery = useQuery({
    queryKey: ['account-groups', 'merchant-filter'],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<AccountGroupRecord>>('/account-groups', {
        params: { per_page: 100 },
      })
      return response.data
    },
    retry: false,
  })
  const groupOptions = groupsQuery.data?.data ?? []

  const importMerchantMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<Merchant>('/merchants/import', {
        fiserv_mid: importForm.fiserv_mid,
        dba_name: importForm.dba_name.trim(),
        legal_name: importForm.legal_name.trim() || null,
        business_email: importForm.business_email.trim() || null,
        business_phone: importForm.business_phone.trim() || null,
      })
      return response.data
    },
    onSuccess: async (merchant) => {
      setShowImportModal(false)
      setImportError(null)
      setImportForm(emptyImportForm)
      await queryClient.invalidateQueries({ queryKey: ['merchants'] })
      navigate(`/merchants/${merchant.id}`)
    },
    onError: (error) => {
      setImportError(apiErrorMessage(error, 'Unable to import merchant.'))
    },
  })

  const updateFilter = (patch: Partial<MerchantFilters>) => {
    setFilters((current) => ({
      ...current,
      ...patch,
      page: 1,
    }))
  }

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    updateFilter({ search: searchInput.trim() || undefined })
  }

  const hasActiveFilters = Boolean(
    filters.search
    || filters.account_group
    || filters.status
    || filters.no_batch_7_days
    || filters.has_open_chargebacks
    || filters.first_batch_this_week
    || (showDataHealth && filters.sync_stale)
    || (showDataHealth && filters.sync_failed)
  )

  const dataHealthLabel = (merchant: Merchant): string => {
    if (merchant.last_transaction_sync_status === 'failed') return 'Data unavailable'
    if (merchant.last_transaction_sync_status === 'skipped') return 'Data pending'
    if (!merchant.last_transaction_sync_at) return 'Data pending'

    const syncedAt = new Date(merchant.last_transaction_sync_at)
    const diffMinutes = Math.max(0, Math.round((Date.now() - syncedAt.getTime()) / 60000))
    if (diffMinutes < 60) return `Updated ${diffMinutes} min ago`
    const diffHours = Math.round(diffMinutes / 60)
    return `Updated ${diffHours} hr ago`
  }

  const dataHealthBadgeClass = (merchant: Merchant): string => {
    if (merchant.last_transaction_sync_status === 'failed') return 'bg-rose-50 text-rose-700 border-rose-200'
    if (merchant.last_transaction_sync_status === 'skipped') return 'bg-gray-100 text-gray-600 border-gray-200'
    if (!merchant.last_transaction_sync_at) return 'bg-amber-50 text-amber-700 border-amber-200'

    const syncedAt = new Date(merchant.last_transaction_sync_at)
    const stale = Date.now() - syncedAt.getTime() > 2 * 60 * 60 * 1000
    return stale
      ? 'bg-orange-50 text-orange-700 border-orange-200'
      : 'bg-emerald-50 text-emerald-700 border-emerald-200'
  }

  const clearFilters = () => {
    setSearchInput('')
    setFilters({
      page: 1,
      per_page: 25,
      sort_by: 'created_at',
      sort_dir: 'desc',
    })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Merchants"
        subtitle="Search by DBA, legal name, email, MID, or account group. Open a merchant to manage account context, services, tickets, reporting, and maintenance."
        action={showImportMerchant ? (
          <Button onClick={() => setShowImportModal(true)}>Import Existing Merchant</Button>
        ) : undefined}
      />

      <SurfaceCard padding="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-2">
            <Input
              type="search"
              aria-label="Search merchants by name, MID, phone, address, or group"
              placeholder="Search name, MID, phone, address, or group..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className="w-72"
            />
            <Button type="submit" variant="secondary">Search</Button>
          </form>

          <select
            aria-label="Filter merchants by group"
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={filters.account_group || ''}
            onChange={(event) => updateFilter({ account_group: event.target.value || undefined })}
          >
            <option value="">All groups</option>
            {groupOptions.map((group) => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>

          <select
            aria-label="Filter merchants by status"
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={filters.status || ''}
            onChange={(event) => updateFilter({ status: event.target.value || undefined })}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="terminated">Terminated</option>
          </select>

          <select
            aria-label="Filter merchants by activity"
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={
              filters.no_batch_7_days
                ? 'no_batch_7_days'
                : filters.has_open_chargebacks
                  ? 'has_open_chargebacks'
                  : filters.first_batch_this_week
                    ? 'first_batch_this_week'
                    : showDataHealth && filters.sync_stale
                      ? 'sync_stale'
                      : showDataHealth && filters.sync_failed
                        ? 'sync_failed'
                        : ''
            }
            onChange={(event) => updateFilter({
              no_batch_7_days: event.target.value === 'no_batch_7_days' || undefined,
              has_open_chargebacks: event.target.value === 'has_open_chargebacks' || undefined,
              first_batch_this_week: event.target.value === 'first_batch_this_week' || undefined,
              sync_stale: (showDataHealth && event.target.value === 'sync_stale') || undefined,
              sync_failed: (showDataHealth && event.target.value === 'sync_failed') || undefined,
            })}
          >
            <option value="">All activity</option>
            <option value="no_batch_7_days">No batch in 7 days</option>
            <option value="has_open_chargebacks">Open chargebacks</option>
            <option value="first_batch_this_week">First batch this week</option>
            {showDataHealth && <option value="sync_stale">Data delayed</option>}
            {showDataHealth && <option value="sync_failed">Data unavailable</option>}
          </select>

          <select
            aria-label="Sort merchants"
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={filters.sort_by || 'created_at'}
            onChange={(event) => updateFilter({ sort_by: event.target.value })}
          >
            <option value="created_at">Newest</option>
            <option value="dba_name">DBA</option>
            <option value="monthly_volume">Monthly volume</option>
            <option value="ytd_volume">YTD volume</option>
            <option value="last_batch_at">Last batch</option>
            <option value="first_batch_at">First batch</option>
            {showDataHealth && <option value="last_transaction_sync_at">Data updated</option>}
          </select>

          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </div>
      </SurfaceCard>

      {isLoading ? (
        <p className="py-8 text-center text-gray-500">Loading merchants...</p>
      ) : isError && !merchants ? (
        <SurfaceCard>
          <div className="py-12 text-center">
            <p className="text-gray-500">Could not load merchants. Try again.</p>
          </div>
        </SurfaceCard>
      ) : merchants && merchants.data.length > 0 ? (
        <SurfaceCard padding="p-0">
          <div className="divide-y divide-slate-100">
            {merchants.data.map((merchant) => (
              <Link
                key={merchant.id}
                to={`/merchants/${merchant.id}`}
                className="grid gap-3 px-4 py-4 transition hover:bg-brand-50/60 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_auto]"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-950">{merchant.dba_name}</p>
                  <p className="truncate text-xs text-slate-500">{merchant.legal_name ?? 'No legal name'}</p>
                  {merchant.account_groups && merchant.account_groups.length > 0 && (
                    <p className="truncate text-xs text-slate-500">
                      {merchant.account_groups.map((group) => group.name).join(', ')}
                    </p>
                  )}
                </div>
                <div className="text-sm text-slate-600">
                  <p className="font-mono text-xs text-slate-500">MID {merchant.fiserv_mid ?? '—'}</p>
                  <StatusPill tone={merchantStatusTone(merchant.status)} className="mt-1 capitalize">
                    {merchant.status}
                  </StatusPill>
                </div>
                <div className="text-sm text-slate-600">
                  <p>{formatCurrencyWithCentsOrDash(merchant.monthly_volume)} monthly</p>
                  <p className="text-xs text-slate-500">
                    Last batch: {merchant.last_batch_at ? new Date(merchant.last_batch_at).toLocaleDateString() : 'No batch'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-start gap-2 md:justify-end">
                  {showDataHealth && (
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${dataHealthBadgeClass(merchant)}`}>
                      {dataHealthLabel(merchant)}
                    </span>
                  )}
                  <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-600">
                    Open chargebacks: {merchant.open_chargebacks_count ?? 0}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </SurfaceCard>
      ) : (
        <SurfaceCard>
          <div className="py-12 text-center">
            <p className="text-gray-500">
              {hasActiveFilters
                ? 'No merchants match the current filters.'
                : 'No boarded merchants yet. Merchants appear when a lead completes the deployment process.'}
            </p>
          </div>
        </SurfaceCard>
      )}

      {merchants && merchants.last_page > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>
            Showing {merchants.from ?? 0}-{merchants.to ?? 0} of {merchants.total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={filters.page === 1}
              onClick={() => setFilters((current) => ({ ...current, page: Math.max(1, (current.page ?? 1) - 1) }))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={(filters.page ?? 1) >= merchants.last_page}
              onClick={() => setFilters((current) => ({ ...current, page: (current.page ?? 1) + 1 }))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {showImportMerchant && showImportModal && (
        <ImportMerchantModal
          form={importForm}
          error={importError}
          pending={importMerchantMutation.isPending}
          onChange={setImportForm}
          onCancel={() => {
            setShowImportModal(false)
            setImportError(null)
          }}
          onSubmit={() => importMerchantMutation.mutate()}
        />
      )}
    </div>
  )
}

function ImportMerchantModal({
  form,
  error,
  pending,
  onChange,
  onCancel,
  onSubmit,
}: {
  form: ImportMerchantForm
  error: string | null
  pending: boolean
  onChange: (form: ImportMerchantForm) => void
  onCancel: () => void
  onSubmit: () => void
}) {
  const setField = (field: keyof ImportMerchantForm, value: string) => {
    onChange({ ...form, [field]: value })
  }

  const normalizedMid = form.fiserv_mid.replace(/\D+/g, '')
  const canSubmit = normalizedMid.length === 16 && form.dba_name.trim().length > 0 && !pending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
      <div className="w-full max-w-xl rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Import Existing Fiserv Merchant</h2>
          <p className="mt-1 text-sm text-gray-500">
            Creates a local CRM merchant record for an account that already exists in YourAccessOne.
          </p>
        </div>
        <form
          className="space-y-4 px-5 py-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (canSubmit) onSubmit()
          }}
        >
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            This does not board, edit, or mutate Fiserv. It only adds the merchant to the CRM so Reporting and profile tools can use the existing MID.
          </div>

          {error && (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {error}
            </p>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="import-fiserv-mid">
              Fiserv MID
            </label>
            <Input
              id="import-fiserv-mid"
              value={form.fiserv_mid}
              onChange={(event) => setField('fiserv_mid', event.target.value)}
              placeholder="4754 792501099788"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Spaces and dashes are OK. The CRM will save this as {normalizedMid || 'a 16-digit MID'}.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <InputField
              id="import-dba-name"
              label="DBA name"
              value={form.dba_name}
              onChange={(value) => setField('dba_name', value)}
              required
            />
            <InputField
              id="import-legal-name"
              label="Legal name"
              value={form.legal_name}
              onChange={(value) => setField('legal_name', value)}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <InputField
              id="import-business-email"
              label="Business email"
              type="email"
              value={form.business_email}
              onChange={(value) => setField('business_email', value)}
            />
            <InputField
              id="import-business-phone"
              label="Business phone"
              value={form.business_phone}
              onChange={(value) => setField('business_phone', value)}
            />
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
            <Button type="button" variant="secondary" autoFocus onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {pending ? 'Importing...' : 'Import Merchant'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function InputField({
  id,
  label,
  value,
  onChange,
  type = 'text',
  required = false,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  required?: boolean
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={id}>
        {label}
      </label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
      />
    </div>
  )
}
